/**
 * <s-credit-popup> — announces the Velle credit (5% cashback) programme.
 * Homepage only (Liquid gate). Once per browser session (sessionStorage).
 * Intro: a brand-blue meteor falls from the top corner, grows as it approaches,
 * hits the centre ("boom": flash, shockwave ring, sparks), then the <dialog>
 * opens from the impact point. Reduced motion: no meteor, plain fade.
 * Never opens over another open dialog (cart drawer, search…): retries a few
 * times, then gives up for this page view. Close / backdrop / Escape are
 * handled by <s-dialog>.
 */
const STORAGE_KEY = 'velle-credit-popup-session';
const RETRY_MS = 6000;
const MAX_RETRIES = 5;

class SCreditPopup extends HTMLElement {
  #timer = null;
  #retries = 0;
  #layer = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || this.#seenThisSession()) return;

    const delay = Number(this.dataset.delay) || 8000;
    this.#timer = setTimeout(() => this.#tryStart(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#layer?.remove();
  }

  #seenThisSession() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* storage unavailable */
    }
  }

  #otherDialogOpen() {
    const open = document.querySelector('dialog[open]');
    return open && open !== this.dialog;
  }

  async #tryStart() {
    if (this.#otherDialogOpen()) {
      if (this.#retries < MAX_RETRIES) {
        this.#retries += 1;
        this.#timer = setTimeout(() => this.#tryStart(), RETRY_MS);
      }
      return;
    }
    if (this.dialog.open) return;
    this.#markSeen();

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) {
      try {
        await this.#meteor();
      } catch {
        /* animation failure must never block the popup */
      }
    }
    if (this.#otherDialogOpen()) return;
    this.#open(reduce);
  }

  #brand() {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--color-interactive').trim();
    return value || '#48728A';
  }

  #el(styles, parent) {
    const node = document.createElement('div');
    Object.assign(node.style, styles);
    (parent || this.#layer).appendChild(node);
    return node;
  }

  async #meteor() {
    const brand = this.#brand();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cx = vw / 2;
    const cy = vh / 2;
    const startX = vw + 80;
    const startY = -160;
    const angle = (Math.atan2(cy - startY, cx - startX) * 180) / Math.PI;

    this.#layer = document.createElement('div');
    this.#layer.setAttribute('aria-hidden', 'true');
    Object.assign(this.#layer.style, {
      position: 'fixed',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '2147483000',
      overflow: 'hidden',
    });
    document.body.appendChild(this.#layer);

    /* Meteor: rotated group, glowing head at the leading end, fading tail behind. */
    const group = this.#el({
      position: 'absolute',
      left: '0',
      top: '0',
      width: '0',
      height: '0',
      willChange: 'transform',
    });
    this.#el(
      {
        position: 'absolute',
        right: '6px',
        top: '-3px',
        width: '220px',
        height: '6px',
        borderRadius: '999px',
        background: `linear-gradient(to right, transparent, color-mix(in srgb, ${brand} 55%, transparent), #ffffff)`,
        filter: 'blur(1px)',
      },
      group,
    );
    this.#el(
      {
        position: 'absolute',
        left: '-14px',
        top: '-14px',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        background: `radial-gradient(circle at 35% 35%, #ffffff 0%, #E5EEF2 30%, ${brand} 75%)`,
        boxShadow: `0 0 24px 8px color-mix(in srgb, ${brand} 70%, transparent), 0 0 60px 20px color-mix(in srgb, ${brand} 35%, transparent)`,
      },
      group,
    );

    const fall = group.animate(
      [
        { transform: `translate(${startX}px, ${startY}px) rotate(${angle}deg) scale(0.35)` },
        { transform: `translate(${cx}px, ${cy}px) rotate(${angle}deg) scale(2.1)` },
      ],
      { duration: 1200, easing: 'cubic-bezier(0.55, 0, 0.9, 0.45)', fill: 'forwards' },
    );
    await fall.finished;
    group.remove();

    /* Boom: flash, shockwave rings, sparks. */
    const flash = this.#el({
      position: 'absolute',
      inset: '0',
      background: `radial-gradient(circle at 50% 50%, #ffffff 0%, #ffffff 12%, color-mix(in srgb, ${brand} 55%, transparent) 40%, transparent 80%)`,
    });
    const flashAnim = flash.animate([{ opacity: 1 }, { opacity: 0.85, offset: 0.25 }, { opacity: 0 }], { duration: 650, easing: 'ease-out', fill: 'forwards' });

    /* Impact shake on the page content (short, small amplitude). */
    document.getElementById('main-content')?.animate(
      [
        { transform: 'translate(0, 0)' },
        { transform: 'translate(-7px, 5px)' },
        { transform: 'translate(6px, -6px)' },
        { transform: 'translate(-4px, 3px)' },
        { transform: 'translate(3px, -2px)' },
        { transform: 'translate(0, 0)' },
      ],
      { duration: 380, easing: 'ease-out' },
    );

    const rings = [0, 110, 230].map((delay) => {
      const ring = this.#el({
        position: 'absolute',
        left: `${cx - 40}px`,
        top: `${cy - 40}px`,
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        border: `4px solid ${brand}`,
        boxShadow: `0 0 30px color-mix(in srgb, ${brand} 60%, transparent)`,
      });
      return ring.animate(
        [
          { transform: 'scale(0.2)', opacity: 1 },
          { transform: 'scale(12)', opacity: 0 },
        ],
        { duration: 850, delay, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'forwards' },
      );
    });

    const sparks = Array.from({ length: 28 }, (_, i) => {
      const a = (i / 28) * Math.PI * 2 + Math.random() * 0.3;
      const dist = 160 + Math.random() * 260;
      const size = 4 + Math.random() * 7;
      const spark = this.#el({
        position: 'absolute',
        left: `${cx - size / 2}px`,
        top: `${cy - size / 2}px`,
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background: i % 3 === 0 ? '#ffffff' : brand,
        boxShadow: `0 0 10px ${brand}`,
      });
      return spark.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          { transform: `translate(${Math.cos(a) * dist}px, ${Math.sin(a) * dist}px) scale(0.2)`, opacity: 0 },
        ],
        { duration: 650 + Math.random() * 250, easing: 'cubic-bezier(0.1, 0.8, 0.3, 1)', fill: 'forwards' },
      );
    });

    /* Open the popup while the shockwave is still expanding. */
    await new Promise((resolve) => setTimeout(resolve, 180));
    Promise.all([flashAnim.finished, ...rings.map((r) => r.finished), ...sparks.map((s) => s.finished)])
      .catch(() => {})
      .finally(() => {
        this.#layer?.remove();
        this.#layer = null;
      });
  }

  #open(reduce) {
    this.dialog.showModal();
    if (reduce) return;
    this.dialog.animate(
      [
        { transform: 'scale(0.3)', opacity: 0, filter: 'blur(10px)' },
        { transform: 'scale(1.03)', opacity: 1, filter: 'blur(0)', offset: 0.7 },
        { transform: 'scale(1)', opacity: 1, filter: 'blur(0)' },
      ],
      { duration: 650, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
  }
}

if (!customElements.get('s-credit-popup')) {
  customElements.define('s-credit-popup', SCreditPopup);
}
